import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, ClipboardList, ArrowUpDown, PieChart, Archive,
  Users, Trash2, Settings, Sun, Moon, LogOut, HardHat, Menu, X, AlertTriangle, FileSignature, Recycle, History, CalendarCheck, Network,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cx } from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
  { to: "/cantieri", label: "Cantieri & Commesse", icon: Building2, testid: "nav-cantieri-link" },
  { to: "/rapportini", label: "Rapportini", icon: ClipboardList, testid: "nav-rapportini-link" },
  { to: "/presenze", label: "Presenze Operai", icon: CalendarCheck, testid: "nav-presenze-link" },
  { to: "/modello-organizzativo", label: "Modello Organizzativo", icon: Network, testid: "nav-modello-link" },
  { to: "/fir", label: "Formulari Rifiuti (FIR)", icon: Recycle, testid: "nav-fir-link" },
  { to: "/documenti", label: "RDO / ODA / CEL", icon: FileSignature, testid: "nav-documenti-link" },
  { to: "/transazioni", label: "Entrate & Uscite", icon: ArrowUpDown, testid: "nav-transazioni-link" },
  { to: "/analisi", label: "Analisi Costi & Margini", icon: PieChart, testid: "nav-analisi-link" },
  { to: "/archivio", label: "Archivio", icon: Archive, testid: "nav-archivio-link" },
  { to: "/scadenze", label: "Scadenze Formazione", icon: AlertTriangle, testid: "nav-scadenze-link" },
  { to: "/utenti", label: "Utenti", icon: Users, testid: "nav-utenti-link", ownerOnly: true },
  { to: "/cronologia", label: "Cronologia Attività", icon: History, testid: "nav-cronologia-link", ownerOnly: true },
  { to: "/cestino", label: "Cestino", icon: Trash2, testid: "nav-cestino-link", ownerOnly: true },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni-link" },
];

function SidebarContent({ onNavigate, isOwner }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500 text-slate-900 shadow-sm">
          <HardHat className="h-5 w-5" strokeWidth={2.4} />
        </div>
        <div className="leading-tight">
          <div className="font-bold text-[14px] leading-tight tracking-tight">Ellevi Costruzioni</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">S.r.l. · Cantieri</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
        {NAV.filter((i) => !i.ownerOnly || isOwner).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              cx(
                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                isActive
                  ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-900 shadow-sm"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )
            }
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            <span className="truncate">{item.to === "/transazioni" && !isOwner ? "Uscite" : item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-5 py-4 border-t border-border">
        <p className="text-[11px] text-muted-foreground">© {new Date().getFullYear()} Ellevi Costruzioni S.r.l.</p>
      </div>
    </div>
  );
}

export default function Layout() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isOwner = user?.role === "owner";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-card border-r border-border z-40">
        <SidebarContent isOwner={isOwner} />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-card border-r border-border animate-fade-up">
            <button className="absolute right-3 top-4 text-muted-foreground" onClick={() => setMobileOpen(false)} data-testid="close-mobile-sidebar">
              <X className="h-5 w-5" />
            </button>
            <SidebarContent isOwner={isOwner} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-card/80 backdrop-blur-md px-4 sm:px-6">
          <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(true)} data-testid="open-mobile-sidebar">
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-button"
            className="text-muted-foreground hover:text-foreground" aria-label="Cambia tema">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-3 pl-2 border-l border-border">
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-sm font-medium">{user?.name || "Utente"}</div>
              <div className="text-[11px] text-muted-foreground">{isOwner ? "Amministratore" : "Dipendente"}</div>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-900 text-xs font-bold">
              {initials}
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button"
              className="text-muted-foreground hover:text-destructive" aria-label="Esci">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
